<template lang="pug">
  div
    .sidebar#sidebar-menu(ref='sidebarMenu')
      .container
        .row.py-3.header-row
          .col
            .small UNIVERSITY OF HAWAII INSTITUTIONAL RESEARCH, ANALYSIS & PLANNING OFFICE
          .col-auto
            button.close.sidebar-close(type='button', data-dismiss='modal', aria-label='Close', @click='closeSidebar')
              span.text-white(aria-hidden='true') &times;
        .row.sidebar-menu-link
          .col
            router-link.nav-link#nav-home(to='/home', @click.native='afterSideBarClick') HOME
        .row
          .col.px-0
            ReportDirectorySidebar#nav-reports(prefix='report', topLevelLabel='REPORTS', dataKey='reportData', :onLinkClick='afterSideBarClick')
        .row
          .col.px-0
            ReportDirectorySidebar#nav-site-links(prefix='site', topLevelLabel='SITE', dataKey='siteData', :onLinkClick='afterSideBarClick')
        .row.sidebar-menu-link
          .col
            router-link.nav-link#nav-community(to='/community', @click.native='afterSideBarClick') COMMUNITY

    .app-content(ref='appContent')
      Loading.mt-5(v-show='!hasEnvironmentData', :showAppMessages='false')

      .d-flex.flex-column.sticky-footer-wrapper#app(v-if='hasEnvironmentData', :class='sessionGetDisplayClass("app")')
        header.no-print#header-top
          NavBar

        //- Login modal
        ModalLoggedIn(v-if='shouldShowModalLoggedIn')

        //- Logged Out modal
        ModalLogout(v-if='shouldShowModalLogout')

        //- ISAT modal
        ModalISat(v-if='shouldRenderISatModal')

        //- ModalMessage modal
        ModalMessage(v-if='shouldShowModalMessage', :data='modalMessageData')

        //- ModalSessionExpired modal
        ModalSessionExpired(v-if='shouldShowModalSessionExpired')

        //- Feedback announcement modal
        ModalFeedbackAnnouncement(v-if='shouldShowFeedbackAnnounce')

        //- Main Page content
        main.px-sm-3.px-xs-1(:class='sessionGetDisplayClass("main")')
          .page-main.text-center(v-if='isGettingUser', :class='sessionGetDisplayClass("content")')
            font-awesome-icon.mx-1#isGettingUser(:icon='["fas", "spinner"]', spin, size='3x')
          router-view(v-else)

          SupportBox(:message='supportMessage', :visible='showSupportButton', :type='supportType')

        Footer.no-print

      //- Back to top button
      a.btn.btn-submit.rounded-circle.back-to-top-button.mr-3.mb-3#back-to-top(v-scroll-to='"#header-top"').no-print
        font-awesome-icon(:icon='["fas", "chevron-up"]')
</template>

<script>
import resource from '@api/resource';
import { bootstrap } from 'vue-gtag';
import { mapGetters } from 'vuex';
// import { DateTime } from 'luxon';
import analytics from '@api/analytics';
import eventBus from '@api/eventBus';
import storage from '@api/storage';
import util from '@api/util';
import Loading from '@components/Loading.vue';
import Footer from './components/Footer.vue';
import NavBar from './components/NavBar.vue';
import ReportDirectorySidebar from '@components/ReportDirectorySidebar.vue';
import SupportBox from '@components/SupportBox';

const BACK_TO_TOP_OFFSET = 50;
const SIDEBAR_MENU_ZERO_OFFSET = '0px';
const DEFAULT_SUPPORT_MESSAGE = 'Need help or not sure where to start? Click the "Start Tour" button for a walk through on this page';

export default {
  name: 'App',
  components: {
    Loading,
    Footer,
    ModalFeedbackAnnouncement: () => import('@components/ModalFeedbackAnnouncement.vue'),
    ModalISat: () => import (/* webpackChunkName: "modalIsat" */ '@components/ModalISat'),
    ModalLoggedIn: () => import (/* webpackChunkName: "modalAuth" */ '@components/ModalLoggedIn'),
    ModalLogout: () => import (/* webpackChunkName: "modalAuth" */ '@components/ModalLogout'),
    ModalMessage: () => import (/* webpackChunkName: "modalMessage" */ '@components/ModalMessage'),
    ModalSessionExpired: () => import (/* webpackChunkName: "modalAuth" */ '@components/ModalSessionExpired'),
    NavBar,
    ReportDirectorySidebar,
    SupportBox
  },
  data: () => ({
    isGettingUser: true,
    iSatMissingAcknowledgement: false,
    overrideShowISat: false, // and override to manually show the ISAT modal
    shouldShowModalLoggedIn: false,
    shouldShowModalLogout: false,
    shouldShowModalSessionExpired: false,
    shouldShowModalMessage: false,
    modalMessageData: null,
    shouldShowFeedbackAnnounce: false,
    showSupportButton: false,
    supportType: '',
    supportMessage: ''
  }),
  computed: {
    ...mapGetters('content', [
      'environment',
      'hasEnvironmentData'
    ]),
    ...mapGetters('session', [
      'session',
      'sessionGetDisplayClass',
      'sessionIsISatCompliant',
      'sessionIsLoggedIn',
      'sessionShownLoggedInModal',
      'sessionShownISatModal',
      'sessionWindowWidth',
      'sessionSideBarWidth',
      'sessionSideBarShouldShiftContent'
    ]),
    shouldRenderISatModal: function () {
      // this controls whether or not the ISAT modal will be shown
      return (this.sessionIsLoggedIn && !this.iSatMissingAcknowledgement && !this.sessionIsISatCompliant && !this.sessionShownISatModal) || this.overrideShowISat;
    }
  },
  methods: {
    authenticated: async function () {
      resource.setIrapo(storage.getBoolean(storage.IS_IRAPO));
      if (this.sessionIsLoggedIn) {
        // make sure to hide the session expired modal
        eventBus.bus.$emit(eventBus.CHANNEL_BUS_MESSAGE, {
          message: eventBus.MESSAGES.SESSION_EXPIRED_MODAL_HIDE
        });

        // check if they opted to not show the public computer warning
        const dontShowModal = storage.getBoolean(storage.GOOGLE_ACCOUNT_DONT_REMIND_ME);
        if (!dontShowModal && !this.sessionShownLoggedInModal) {
          this.shouldShowModalLoggedIn = true;
        }

        // check for ISAT/GCN
        this.iSatMissingAcknowledgement = storage.getBoolean(storage.ISAT_WARNING_ACKNOWLEDGED);

        //check for access roles to non-WAT apps
        try {
          const rolesCls = await resource.getRoleForApp(this.$route.query, 'SMCLS');
          this.$store.dispatch('session/setSessionAppRoles', {"id": "SMCLS", "value": rolesCls});
          this.$store.dispatch('session/setSessionDBSDMUp', true);
        }
        catch (smclsError) {
          console.error(smclsError);
          this.$store.dispatch('session/setSessionDBSDMUp', false);
        }

        // get User Preferences
        try {
          const prefs = await resource.get('api/w/me');
          this.$store.dispatch('session/setSessionUserPrefs', prefs);
        }
        catch (userErr) {
          console.error(userErr);
        }
      }
      else {
        this.shouldShowModalLogout = true;

        // reset this since we are logging out
        this.$store.dispatch('session/setSessionShownLoggedInModal', false);
      }
    },
    scrollToTop: function () {
      $('html,body').scrollTop(0);
    },
    afterSideBarClick: function () {
      if (!this.sessionSideBarShouldShiftContent) {
        this.closeSidebar();
      }
      this.scrollToTop();
    },
    toggleSidebar: function () {
      const widthOffset = `${this.sessionSideBarWidth}px`;

      // is open
      let isNowOpen = false;
      this.$refs.sidebarMenu.style.width = widthOffset;
      if (this.$refs.sidebarMenu.style.left === SIDEBAR_MENU_ZERO_OFFSET) {
        this.$refs.sidebarMenu.style.left = `-${widthOffset}`;
        this.$refs.appContent.style.marginLeft = SIDEBAR_MENU_ZERO_OFFSET;
      }
      else {
        isNowOpen = true;
        this.$refs.sidebarMenu.style.left = SIDEBAR_MENU_ZERO_OFFSET;
        if (this.sessionSideBarShouldShiftContent) {
          this.$refs.appContent.style.marginLeft = widthOffset;
        }
      }

      // set it in the store
      this.$store.dispatch('session/setSessionIsSideBarOpen', isNowOpen);
    },
    closeSidebar: function () {
      eventBus.bus.$emit(eventBus.CHANNEL_TOGGLE_SIDEBAR);
    },
    windowResizeEvent: function () {
      this.$store.dispatch('session/setSessionWindowWidth');
    }
  },
  created: function () {
    // bootstrap the analytics only if debug mode and not IRAPO
    const isDebugMode = process.env.VUE_APP_DEBUG_ANALYTICS === 'true' || false;
    const isIrapo = storage.getBoolean(storage.IS_IRAPO);
    if (isDebugMode || !isIrapo) {
      bootstrap().then(() => {
        analytics.setAnalyticsReady(this.$gtag);
      });
    }

    // set it on the resource
    resource.setIrapo(isIrapo);

    const displayMode = storage.getItem(storage.DISPLAY_MODE);
    if (displayMode) {
      this.$store.dispatch('session/setDisplayMode', displayMode);
    }

    // listen for the authenticated event
    eventBus.bus.$on(eventBus.CHANNEL_AUTHENTICATED, () => {
      this.authenticated();
    });

    // listen for the generic bus message and handle modals accordingly
    eventBus.bus.$on(eventBus.CHANNEL_BUS_MESSAGE, (value) => {
      if (value.message === eventBus.MESSAGES.ISAT_MODAL) {
        this.overrideShowISat = true;
      }
      else if (value.message === eventBus.MESSAGES.ISAT_MODAL_HIDE) {
        this.overrideShowISat = false;
      }
      else if (value.message === eventBus.MESSAGES.LOGOUT_MODAL_HIDE) {
        this.shouldShowModalLogout = false;
      }
      else if (value.message === eventBus.MESSAGES.LOGGED_IN_MODAL_HIDE) {
        this.shouldShowModalLoggedIn = false;
      }
      else if (value.message === eventBus.MESSAGES.SESSION_EXPIRED_MODAL_SHOW) {
        this.shouldShowModalSessionExpired = true;
      }
      else if (value.message === eventBus.MESSAGES.MESSAGE_MODAL_HIDE) {
        this.shouldShowModalMessage = false;
      }
      else if (value.message === eventBus.MESSAGES.MESSAGE_MODAL_SHOW) {
        this.shouldShowModalMessage = true;
        this.modalMessageData = value.data;
      }
    });

    // listen for when the sidebar is toggled
    eventBus.bus.$on(eventBus.CHANNEL_TOGGLE_SIDEBAR, this.toggleSidebar);

    // we try to get the User first from the OIDC client, once that's done we can load the rest of the page
    eventBus.bus.$on(eventBus.CHANNEL_USER_LOADED, () => {
      this.isGettingUser = false;
    });

    eventBus.bus.$on(eventBus.CHANNEL_GLOBAL_HELP_BUTTON, (options) => {
      this.showSupportButton = options.visible;
      this.supportType = options.type;
      this.supportMessage = options.message || DEFAULT_SUPPORT_MESSAGE;
    });

    // so we cand determine the size of the window
    window.addEventListener('resize', this.windowResizeEvent);
  },
  mounted: function () {
    const topButton = document.getElementById('back-to-top');
    window.onscroll = () => {
      if (window.pageYOffset > BACK_TO_TOP_OFFSET) {
        topButton.classList.add('show-button');
      }
      else {
        topButton.classList.remove('show-button');
      }
      eventBus.bus.$emit(eventBus.WINDOW_SCROLL);
    }

    // set the sidebar dimensions
    this.$refs.sidebarMenu.style.left = `-${util.SIDEBAR_WIDTH}px`;
    this.$refs.sidebarMenu.style.width = `${util.SIDEBAR_WIDTH}px`;

    // check for feedback announcement
    // turning off feedback modal until we bring it back later
    // const feedbackExpires = storage.getItem(storage.FEEDBACK_ANNOUNCEMENT);
    // this.shouldShowFeedbackAnnounce = this.environment.ENVIRONMENT !== 'qa' && (!feedbackExpires || DateTime.now() > DateTime.fromISO(feedbackExpires));
  },
  beforeDestroy: function () {
    eventBus.bus.$off(eventBus.CHANNEL_AUTHENTICATED);
    eventBus.bus.$off(eventBus.CHANNEL_BUS_MESSAGE);
    eventBus.bus.$off(eventBus.CHANNEL_TOGGLE_SIDEBAR);

    window.removeEventListener('resize', this.windowResizeEvent);
  }
}
</script>

<style lang="stylus">
  @import './assets/app.styl'
  @page
    size 8.5 x 11 portrait
    margin .25in

  a
    color link-color

  .sticky-footer-wrapper
    min-height 100vh
  body
    font-family: 'Open Sans', 'Tahoma', sans-serif;

  .back-to-top-button
    position fixed
    right 0
    bottom 0
    z-index 1030
    transform translateY(100px)
    transition all .5s ease
    opacity 0
  .show-button
    opacity 1
    transform translateY(0)

  .dropdown-item
    // color white
    background-color transparent
    &:hover
      color highlight-color
      background-color dark-background-color

  .sidebar
    height 100%
    position fixed
    z-index 9999
    top 0
    background-color dark-background-color
    overflow-x hidden
    transition 0.5s
    color white
    overscroll-behavior-block contain

    .header-row
      background-color #352D39

    .nav-link
      color white
      background-color transparent
      &.main-active, &.router-link-active, &:hover
        color highlight-color
      padding-left 0
      padding-right 0

  .sidebar-close
    font-size 2rem

  .app-content
    transition margin-left .5s

  .sidebar-menu-link
    &:hover
      background-color dark-background-color-active

  .searchbar.dark
    background: dark-mode-background-color
    color: dark-mode-text-color
  .searchbar.dark:focus
    background: dark-mode-background-color
    color: dark-mode-text-color

  .vbt-autcomplete-list.dark
    .vbst-item
      background: dark-mode-background-color
      color: dark-mode-text-color
      border: 1px solid dark-mode-text-color
      &:hover
        background: dark-mode-blue-highlight

</style>
